import React, { ReactNode, FC, useContext, useMemo, Context } from 'react';
import { ThemeContext } from 'styled-components';

import { SCP } from 'ia-react-core/~/interfaces/SCP';
import FlexRowPadding from './FlexRowPadding';
import { StyledRow } from './FlexRow.styles';

export interface FlexRowProps extends SCP<'div'>{
  /** Content of the row. Should usually be a series of [FormCol](/Core/Components/Form/FormCol). */
  children?: ReactNode;
  /** Defines the default col of every [FormCol](/Core/Components/Form/FormCol) inside the row. */
  defaultCol?: number;
  /** Defines the default `minColWidth` of every [FormCol](/Core/Components/Form/FormCol) inside the row. */
  minColWidth?: number;
  /** Controls the width of the padding elements. Padding elements are used to force columns to collapse when there is no space available. */
  paddingSize?: number;
  /** The Context to read from and expose the columns to. */
  ColContext: Context<{
    defaultCol?: number;
    minColWidth?: number;
    gutterWidth?: number;
    grouped?: boolean;
    importance?: string;
  }>;
  /** A ref to the column element. */
  rowRef?: any;
  /* Width of the gutter between rows. */
  gutterWidth?: number;
  margin?: number | string;
  styles?: string;
  additionalColContext?: object;
}

const FlexRowDefaultProps: Partial<FlexRowProps> = {
  margin: '0 -16px',
};

const FlexRow: FC<FlexRowProps> = ({
  children, defaultCol, minColWidth, paddingSize, ColContext, gutterWidth, rowRef, additionalColContext, ...props
}) => {
  const {
    layout: {
      defaultCol: baseDefaultCol,
      defaultColWidth,
    },
  } = useContext(ThemeContext);

  const {
    defaultCol: parentDefaultCol = baseDefaultCol,
    minColWidth: parentMinColWidth = defaultColWidth,
    ...colContext
  } = useContext(ColContext) || {};

  const newColContext = useMemo(() => ({
    defaultCol: defaultCol || parentDefaultCol,
    minColWidth: minColWidth || parentMinColWidth,
    grouped: true,
    gutterWidth,
    ...colContext,
    ...additionalColContext,
  }), [additionalColContext, colContext, defaultCol, gutterWidth, minColWidth, parentDefaultCol, parentMinColWidth]);

  return (
    <ColContext.Provider value={newColContext}>
      <StyledRow {...props} ref={rowRef}>
        {children}
        <FlexRowPadding
          paddingSize={paddingSize}
          defaultCol={defaultCol || parentDefaultCol}
          minColWidth={minColWidth || parentMinColWidth}
        />
      </StyledRow>
    </ColContext.Provider>
  );
};

FlexRow.defaultProps = FlexRowDefaultProps;

export default FlexRow;
